import { useEffect, useMemo, useRef } from 'react';
import { hashKey, InfiniteData, QueryKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { createInfiniteQueryOptions, LightweightQueryOptions } from '@/lib/api-calls.js';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination.js';

type PaginatedResult = { total: number; pageIndex: number; pageSize: number };

/**
 * Hook to use an infinite query with a regular query function.
 * This adapts a paginated query function for infinite scrolling.
 *
 * @example
 * ```tsx
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteScrollQuery(
 *   (pageIndex, pageSize) => getStudents({ pageIndex, pageSize, filters: { query: 'test' } }),
 *   (result) => result.students,
 * );
 * ```
 */
export function useInfiniteScrollQuery<Result extends PaginatedResult>(
  requestQuery: (pageIndex: number, pageSize: number) => LightweightQueryOptions<Promise<Result>>,
  getDataArray: (result: Result) => unknown[],
  pageSize: number = DEFAULT_PAGE_SIZE,
  options?: {
    refetchInterval?: number | false;
    refetchIntervalInBackground?: boolean;
  },
) {
  const infiniteQueryOptions = useMemo(
    () => createInfiniteQueryOptions<Result>(requestQuery, getDataArray, pageSize),
    [requestQuery, getDataArray, pageSize],
  );
  const queryKey = useMemo<QueryKey>(
    () => [...infiniteQueryOptions.queryKey, 'infinite'],
    [infiniteQueryOptions.queryKey],
  );

  const query = useInfiniteQuery({
    ...infiniteQueryOptions,
    queryKey,
    ...options,
  });

  return { ...query, queryKey };
}

/**
 * Keeps the first occurrence of each id. Refreshing pages independently can shift offsets so the
 * same row appears in two loaded pages.
 */
export function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export const DEFAULT_MAX_REFRESHED_PAGES = 3;

/**
 * Picks the loaded pages to refresh: always page 0 (it carries `total`), then the requested
 * pages, without duplicates or pages that are not loaded, capped at `maxPages`.
 */
export function selectPagesToRefresh(
  pageIndexes: number[],
  loadedPageCount: number,
  maxPages: number = DEFAULT_MAX_REFRESHED_PAGES,
): number[] {
  const unique = [...new Set([0, ...pageIndexes])];
  return unique.filter((pageIndex) => pageIndex >= 0 && pageIndex < loadedPageCount).slice(0, maxPages);
}

/**
 * Replaces the refreshed pages in place and propagates the fresh `total` (from page 0) to every
 * page, so `getNextPageParam` reflects rows added or removed since those pages were loaded.
 */
export function mergeRefreshedPages<Result extends PaginatedResult, PageParam>(
  data: InfiniteData<Result, PageParam>,
  pageIndexes: number[],
  freshPages: Result[],
): InfiniteData<Result, PageParam> {
  const pages = [...data.pages];
  pageIndexes.forEach((pageIndex, i) => {
    if (pageIndex < pages.length) {
      pages[pageIndex] = freshPages[i];
    }
  });

  const freshTotal = freshPages[pageIndexes.indexOf(0)]?.total;
  return {
    ...data,
    pages: freshTotal == null ? pages : pages.map((page) => ({ ...page, total: freshTotal })),
  };
}

/**
 * Polls a bounded set of pages of an infinite query (page 0 plus `pageIndexes`) and merges them
 * into the cache. Use instead of `refetchInterval`, which refetches every loaded page sequentially.
 */
export function useBoundedInfiniteRefresh<Result extends PaginatedResult>({
  queryKey,
  requestQuery,
  pageSize,
  pageIndexes,
  intervalMs,
  enabled = true,
  maxPages = DEFAULT_MAX_REFRESHED_PAGES,
}: {
  queryKey: QueryKey;
  requestQuery: (pageIndex: number, pageSize: number) => LightweightQueryOptions<Promise<Result>>;
  pageSize: number;
  pageIndexes: number[];
  intervalMs: number;
  enabled?: boolean;
  maxPages?: number;
}) {
  const queryClient = useQueryClient();
  const latest = useRef({ queryKey, requestQuery, pageSize, pageIndexes });

  useEffect(() => {
    latest.current = { queryKey, requestQuery, pageSize, pageIndexes };
  }, [queryKey, requestQuery, pageSize, pageIndexes]);

  useEffect(() => {
    if (!enabled) return;
    let isRefreshing = false;

    const refresh = async () => {
      if (isRefreshing || document.hidden) return;
      const { queryKey: key, requestQuery: request, pageSize: size, pageIndexes: indexes } = latest.current;
      if (queryClient.isFetching({ queryKey: key, exact: true }) > 0) return;

      const loadedPageCount = queryClient.getQueryData<InfiniteData<Result>>(key)?.pages.length ?? 0;
      const pagesToRefresh = selectPagesToRefresh(indexes, loadedPageCount, maxPages);
      if (pagesToRefresh.length === 0) return;

      isRefreshing = true;
      try {
        const freshPages = await Promise.all(pagesToRefresh.map((pageIndex) => request(pageIndex, size).queryFn()));
        if (hashKey(latest.current.queryKey) !== hashKey(key)) return;
        queryClient.setQueryData<InfiniteData<Result>>(key, (old) =>
          old ? mergeRefreshedPages(old, pagesToRefresh, freshPages) : old,
        );
      } catch {
        // A failed poll keeps the current rows; the next tick retries.
      } finally {
        isRefreshing = false;
      }
    };

    const intervalId = setInterval(() => void refresh(), intervalMs);
    return () => clearInterval(intervalId);
  }, [enabled, intervalMs, maxPages, queryClient]);
}
